import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import { useT } from "../../i18n/LanguageProvider";
import { useAuth } from "../../state/AuthContext";
import { NewPasswordForm } from "./NewPasswordForm";
import {
  AccountShell,
  errorStatus,
  useAccountLanguage,
  useLinkPreview,
  useLinkToken,
} from "./accountLink";

/**
 * G-136 — `/account/reset#token=…`, from a password-reset email (asked for
 * on the sign-in page, or sent by a manager or owner from Team). The new
 * password ends every CRM session, so the page ends by asking the person to
 * sign in with it rather than signing them in.
 */
export function AccountResetPage() {
  const token = useLinkToken();
  const link = useLinkPreview(token, ["PASSWORD_RESET"]);
  useAccountLanguage(link.kind === "ready" ? link.preview.language : null);
  const { t } = useT();
  const { user, endCrmSessionLocally } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (link.kind === "loading") {
    return (
      <AccountShell>
        <p className="page-subtitle account-center">{t("account.loading")}</p>
      </AccountShell>
    );
  }

  if (done || link.kind === "unusable") {
    return (
      <AccountShell>
        <h1 className="page-title account-center">
          {done ? t("account.resetDoneTitle") : t("account.linkUnusableTitle")}
        </h1>
        <p className="account-center">{done ? t("account.resetDone") : t("account.linkUnusable")}</p>
        <Link className="button" to="/login">
          {t("account.goToSignIn")}
        </Link>
      </AccountShell>
    );
  }

  const submit = async (password: string) => {
    setError(null);
    setBusy(true);
    try {
      await api.completePasswordReset(token, password);
      // The reset ended every session of this person on the server. If this
      // browser is signed in as them, end it here too, straight to sign-in —
      // otherwise "Go to sign in" bounces into a session that is already dead.
      if (user?.id === link.preview.userId) {
        endCrmSessionLocally(t("account.resetDone"));
        return;
      }
      setDone(true);
    } catch (err: unknown) {
      setError(errorStatus(err) === 410 ? t("account.linkUnusable") : t("account.genericError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccountShell>
      <h1 className="page-title account-center">{t("account.resetTitle")}</h1>
      <p className="account-center">{t("account.resetLead", { email: link.preview.email })}</p>
      <NewPasswordForm
        submitLabel={t("account.resetSubmit")}
        error={error}
        busy={busy}
        onSubmit={(password) => void submit(password)}
      />
    </AccountShell>
  );
}
